import React, { useState } from 'react';
import { ToolCallDisplay } from '../../types/chat';
import { normalizeToolCall, resolveToolCardKind } from '../../utils/toolCalls';
import ToolCardFrame, { ToolCardStatus } from './tool-cards/ToolCardFrame';
import ToolCardProgress from './tool-cards/ToolCardProgress';
import ProposalCardBody from './tool-cards/ProposalCardBody';
import ImageResultGrid from './tool-cards/ImageResultGrid';
import VideoResultPlayer from './tool-cards/VideoResultPlayer';
import CarouselResultStrip, { CarouselProgressInfo } from './tool-cards/CarouselResultStrip';
import ToolErrorBody from './tool-cards/ToolErrorBody';
import { GENERATION_TOOLS, toolCardMeta } from './tool-cards/toolCardMeta';

interface ToolActivityCardProps {
  toolCalls: ToolCallDisplay[];
  onToolAction?: (text: string) => void;
  actionsDisabled?: boolean;
  executedProposalCodes?: string[];
}

const IMAGE_PAGE_LINK = { href: '/ads/banner', label: 'バナーページで開く' };

interface ToolCardItemProps {
  card: ToolCallDisplay;
  onToolAction?: (text: string) => void;
  actionsDisabled: boolean;
  executedProposalCodes: string[];
}

const ToolCardItem: React.FC<ToolCardItemProps> = ({ card, onToolAction, actionsDisabled, executedProposalCodes }) => {
  // kind は永続化レコードに無いことがあるため、card.kind ではなく推定を通す。
  const kind = resolveToolCardKind(card);
  const isError = card.status === 'error';
  const isBusy = card.status === 'running' || card.status === 'progress';
  const canRetry = GENERATION_TOOLS.has(card.name);
  // 子（動画・カルーセル）はポーリングで状態が変わるので、状態ピルへ持ち上げる。
  const [childStatus, setChildStatus] = useState<string | null>(null);
  const [carousel, setCarousel] = useState<CarouselProgressInfo | null>(null);
  const carouselAllFailed = carousel !== null && carousel.allDone && carousel.total > 0 && carousel.failedCount === carousel.total;

  const proposal = kind === 'proposal' ? card.proposal ?? null : null;
  const videos = card.videos ?? [];
  const jobs = card.jobs ?? [];

  const proposalExecuted = proposal !== null && executedProposalCodes.includes(proposal.code);
  const proposalExpired = proposal !== null && !proposalExecuted && Date.parse(proposal.expires_at) < Date.now();

  let status: ToolCardStatus = 'completed';
  if (isError) status = 'error';
  else if (isBusy) status = 'running';
  else if (kind === 'proposal') status = proposalExecuted ? 'executed' : proposalExpired ? 'expired' : 'awaiting';
  // 追うジョブがある場合だけ「生成中」。空なら追跡できず永久にスピナーが回るので完了扱いにする。
  else if (kind === 'video' && card.job_id && videos.length > 0) {
    status =
      childStatus === 'failed' || childStatus === 'cancelled'
        ? 'error'
        : childStatus === 'unknown'
          ? 'unknown'
          : childStatus === 'completed'
            ? 'completed'
            : 'generating';
  } else if (kind === 'carousel' && jobs.length > 0) {
    // 全スライド失敗は失敗、一部失敗は完了（タイル側で個別に失敗を示す）。
    status = carouselAllFailed ? 'error' : carousel?.allDone ? 'completed' : 'generating';
  }

  const meta = toolCardMeta(card.name, kind, status === 'error');
  // 提案カードのチップは tool_result の chips に載るが、履歴レコードでは proposal 側にしか無いことがある。
  const chips = card.chips ?? proposal?.chips;
  const showSummary = Boolean(card.summary) && (kind === 'info' || (kind === 'video' && !card.job_id) || isError);

  return (
    <ToolCardFrame label={card.label} meta={meta} status={status} chips={chips} argsSummary={isBusy ? card.args_summary : undefined}>
      {isBusy && card.status === 'progress' && typeof card.progress === 'number' && (
        <ToolCardProgress percent={card.progress} hint={card.args_summary ?? undefined} />
      )}

      {isError && (
        <ToolErrorBody message={card.summary ?? '失敗しました'} canRetry={canRetry} disabled={actionsDisabled} onToolAction={onToolAction} />
      )}

      {!isError && !isBusy && showSummary && <p className="text-xs text-gray-600">{card.summary}</p>}

      {!isError && !isBusy && proposal !== null && (
        <ProposalCardBody
          proposal={proposal}
          executed={proposalExecuted}
          expired={proposalExpired}
          disabled={actionsDisabled}
          onToolAction={onToolAction}
        />
      )}

      {!isError && !isBusy && kind === 'image' && (
        <>
          {card.summary && <p className="mb-2 text-xs text-gray-600">{card.summary}</p>}
          <ImageResultGrid jobId={card.job_id} images={card.images ?? []} pageLink={card.name === 'generate_banner' ? IMAGE_PAGE_LINK : null} />
        </>
      )}

      {!isError &&
        !isBusy &&
        kind === 'video' &&
        videos.length > 0 &&
        (card.job_id ? (
          <VideoResultPlayer
            video={videos[0]}
            canRetry={canRetry}
            onToolAction={onToolAction}
            actionsDisabled={actionsDisabled}
            onStatusChange={setChildStatus}
            showAdsPageLink={card.name !== 'chat_video'}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {videos.map((v, i) => (
              <VideoResultPlayer key={`${v.video_id ?? v.job_id ?? i}`} video={v} compact />
            ))}
          </div>
        ))}

      {!isError && !isBusy && kind === 'carousel' && jobs.length > 0 && (
        <>
          {carouselAllFailed && (
            <div className="mb-2">
              <ToolErrorBody message="カルーセルの生成に失敗しました。" canRetry={canRetry} disabled={actionsDisabled} onToolAction={onToolAction} />
            </div>
          )}
          <CarouselResultStrip jobs={jobs} onProgress={setCarousel} />
        </>
      )}
    </ToolCardFrame>
  );
};

/**
 * AI メッセージのツール実行（商品登録・バナー/動画/カルーセル生成・提案）を本文の上にカードで表示する。
 */
const ToolActivityCard: React.FC<ToolActivityCardProps> = ({ toolCalls, onToolAction, actionsDisabled = false, executedProposalCodes = [] }) => {
  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="mb-3 flex flex-col gap-2">
      {toolCalls.map((card) => (
        // 履歴（永続化レコード）は status / label / kind を持たないため描画前に正規化する
        <ToolCardItem
          key={card.call_id}
          card={normalizeToolCall(card)}
          onToolAction={onToolAction}
          actionsDisabled={actionsDisabled}
          executedProposalCodes={executedProposalCodes}
        />
      ))}
    </div>
  );
};

export default ToolActivityCard;
